import React, { useState } from 'react';

import { Alert, Form, Input, Modal, Typography, Upload, message } from 'antd';

import { InboxOutlined } from '@ant-design/icons';

import type { UploadFile } from 'antd/es/upload/interface';

import { api } from '../../../http/API';

const { Dragger } = Upload;
const { Text } = Typography;

interface Props {
    open: boolean;
    onCancel: () => void;
    onSaved: () => void;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 МБ, как на сервере

const ACCEPT = '.geojson,.json,.kml,.zip,.csv,.tsv,.txt';

const FieldImportModal = (props: Props) => {
    const { open, onCancel, onSaved } = props;
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const reset = () => {
        form.resetFields();
        setFileList([]);
    };

    const beforeUpload = (file: File) => {
        if (file.size > MAX_FILE_SIZE) {
            message.error(`Файл больше ${MAX_FILE_SIZE / 1024 / 1024} МБ`);
            return Upload.LIST_IGNORE;
        }
        setFileList([
            {
                uid: file.name + '-' + file.size,
                name: file.name,
                status: 'done',
                size: file.size,
                originFileObj: file as any,
            } as UploadFile,
        ]);
        return false; // не грузим автоматически
    };

    const onRemove = () => {
        setFileList([]);
    };

    const handleOk = () => {
        if (fileList.length === 0) {
            message.error('Выберите файл');
            return;
        }
        const raw = fileList[0].originFileObj as unknown as File | undefined;
        if (!raw) {
            message.error('Не удалось прочитать файл');
            return;
        }
        form.validateFields()
            .then((values) => {
                const name = (values.name || '').trim();
                const location = (values.location || '').trim();
                setLoading(true);
                api
                    .ImportField(raw, name || undefined, location || undefined)
                    .then((fields) => {
                        if (!fields || fields.length === 0) {
                            message.warning('Файл обработан, но полей не создано');
                        } else if (fields.length === 1) {
                            const f = fields[0];
                            message.success(`Поле «${f.name || '#' + f.id}» создано`);
                        } else {
                            message.success(`Создано полей: ${fields.length}`);
                        }
                        reset();
                        onSaved();
                    })
                    .catch((err) => {
                        const msg =
                            err?.response?.data?.message ||
                            'Не удалось импортировать файл';
                        message.error(msg);
                    })
                    .finally(() => setLoading(false));
            })
            .catch(() => undefined);
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    return (
        <Modal
            title="Импорт поля из файла"
            open={open}
            onCancel={handleCancel}
            onOk={handleOk}
            confirmLoading={loading}
            okText="Импортировать"
            cancelText="Отмена"
            width={640}
            destroyOnClose
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Поддерживаемые форматы"
                description={
                    <span>
                        <Text code>.geojson</Text> / <Text code>.json</Text> — GeoJSON
                        FeatureCollection, Polygon / MultiPolygon<br />
                        <Text code>.kml</Text> — Yandex Map Constructor, Google Maps<br />
                        <Text code>.zip</Text> — Shapefile (в ZIP: .shp, .shx, .dbf, .prj)<br />
                        <Text code>.csv</Text> — список вершин с колонками <Text code>lat,lon</Text>
                        <br />
                        Лимит файла: 25 МБ.
                    </span>
                }
            />

            <Dragger
                beforeUpload={beforeUpload}
                onRemove={onRemove}
                fileList={fileList}
                accept={ACCEPT}
                maxCount={1}
                multiple={false}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Перетащите файл сюда или кликните для выбора</p>
                <p className="ant-upload-hint">
                    Один файл. Формат определится автоматически по расширению и содержимому.
                </p>
            </Dragger>

            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item
                    name="name"
                    label="Название поля"
                    extra="Опционально. Если пусто — возьмётся имя файла без расширения."
                >
                    <Input placeholder="Например: Поле у реки" />
                </Form.Item>
                <Form.Item name="location" label="Локация">
                    <Input placeholder="Опционально" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FieldImportModal;
